import React, { useEffect, useRef } from "react";
import { formatUsd } from "@veyso/core";
import type { LanguageView } from "../api.js";
import { ProgressLine } from "../components/ProgressLine.js";
import { languageStepLabel, languageSteps } from "../languages/languageSteps.js";
import { emailNote } from "../lib/eta.js";

/**
 * A language being made, opened from its tab: the step, the bar, time left,
 * the steps done and to come, what is held, and that nobody has to wait here.
 */
export const LanguageProgress: React.FC<{
  language: LanguageView;
  email: string | null;
  /** Whether the jobs feed says this language's job will email the person looking. */
  emailsYou: boolean;
  onClose: () => void;
  /**
   * What counts as inside for an outside click. The switch passes its whole
   * wrapper, so a press on a tab is the tab's own to handle: otherwise the
   * press closes the popover and the click then reopens it.
   */
  boundary?: React.RefObject<HTMLElement | null>;
  /** Which edge of the switch the popover hangs from. */
  align?: "left" | "right";
  /** A free-forever team: nothing is held, so the popover does not say so. */
  free?: boolean;
}> = ({ language, email: address, emailsYou, onClose, boundary, align = "left", free = false }) => {
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => event.key === "Escape" && onClose();
    const onDown = (event: MouseEvent) => {
      const inside = boundary?.current ?? box.current;
      if (inside && !inside.contains(event.target as Node)) onClose();
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
    };
  }, [onClose, boundary]);

  const held =
    language.status !== "revoicing" && !free ? `Holding ${formatUsd(language.priceUsd)} until it's ready. ` : "";
  // A revoice sends nothing, so it never promises an email.
  const email = emailsYou && language.status !== "revoicing" ? emailNote(address, `${language.name} is ready`) : null;

  return (
    <div
      ref={box}
      role="dialog"
      aria-label={`${language.name} progress`}
      className={`absolute ${align === "right" ? "right-0" : "left-0"} top-[calc(100%+8px)] z-30 flex w-[360px] max-w-[calc(100vw-32px)] flex-col gap-3.5 rounded-[12px] border border-[var(--edge-strong)] bg-bay-850 p-[18px] shadow-xl`}
    >
      <span className="font-semibold text-ink-100">
        {language.nativeName}{" "}
        <span className="text-[13px] font-normal text-ink-400">
          {language.name} · {language.wholeVideo ? "the whole video" : "voice and subtitles"}
        </span>
      </span>
      <ProgressLine
        id={language.jobId ?? `language-${language.code}`}
        label={languageStepLabel(language)}
        progress={language.progress}
        eta={language.eta}
      />
      <ol className="m-0 flex list-none flex-col gap-1.5 p-0 text-[13px] text-ink-300">
        {languageSteps(language).map((step) => (
          <li key={step.label} className={`flex gap-2 ${step.state === "current" ? "font-medium text-ink-100" : ""}`}>
            <span aria-hidden>{step.state === "done" ? "✓" : step.state === "current" ? "●" : "○"}</span>
            {step.label}
          </li>
        ))}
      </ol>
      {held || email ? (
        <p className="border-t border-[var(--edge)] pt-3 text-[13px] leading-relaxed text-ink-300">
          {held}
          {email}
        </p>
      ) : null}
    </div>
  );
};
